import React, { useState, useCallback } from 'react'
import styled from 'styled-components'
import { Icon } from '../../../atoms'
import type { ZoneQAFeedbackType } from '@popety-io/types'

export interface ZoneQAFeedbackProps {
  messageId: string
  onSubmit: (
    messageId: string,
    rating: number,
    feedbackType?: ZoneQAFeedbackType,
    comment?: string
  ) => Promise<void>
  disabled?: boolean
  translations?: {
    helpful?: string
    notHelpful?: string
    thankYou?: string
    feedbackError?: string
  }
}

const DEFAULT_TRANSLATIONS = {
  helpful: 'Helpful',
  notHelpful: 'Not helpful',
  thankYou: 'Thank you for your feedback!',
  feedbackError: 'Failed to submit feedback',
}

const FeedbackContainer = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xsmall};
  padding-top: ${({ theme }) => theme.spacing.xsmall};
`

const FeedbackButton = styled.button<{
  $isSelected?: boolean
  $variant: 'positive' | 'negative'
}>`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.micro};
  padding: ${({ theme }) => theme.spacing.micro}
    ${({ theme }) => theme.spacing.xsmall};
  border-radius: ${({ theme }) => theme.borderRadius.default};
  border: 1px solid ${({ theme }) => theme.colors.border.default};
  background: ${({ theme, $isSelected, $variant }) =>
    $isSelected
      ? $variant === 'positive'
        ? theme.colors.successLight
        : theme.colors.warningLight
      : theme.colors.background.primary};
  color: ${({ theme, $isSelected, $variant }) =>
    $isSelected
      ? $variant === 'positive'
        ? theme.colors.success
        : theme.colors.warning
      : theme.colors.text.secondary};
  font-size: ${({ theme }) => theme.typography.fontSize.micro};
  cursor: pointer;
  transition: ${({ theme }) => theme.transitions.fast};

  &:hover:not(:disabled) {
    border-color: ${({ theme, $variant }) =>
      $variant === 'positive' ? theme.colors.success : theme.colors.warning};
    color: ${({ theme, $variant }) =>
      $variant === 'positive' ? theme.colors.success : theme.colors.warning};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  svg {
    width: 14px;
    height: 14px;
  }
`

const ThankYouMessage = styled.span`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.micro};
  color: ${({ theme }) => theme.colors.success};
  font-size: ${({ theme }) => theme.typography.fontSize.micro};

  svg {
    width: 14px;
    height: 14px;
  }
`

const ErrorMessage = styled.span`
  color: ${({ theme }) => theme.colors.danger};
  font-size: ${({ theme }) => theme.typography.fontSize.micro};
`

export const ZoneQAFeedback: React.FC<ZoneQAFeedbackProps> = ({
  messageId,
  onSubmit,
  disabled = false,
  translations,
}) => {
  const t = { ...DEFAULT_TRANSLATIONS, ...translations }
  const [feedbackState, setFeedbackState] = useState<
    'none' | 'positive' | 'negative' | 'submitted' | 'error'
  >('none')
  const [isSubmitting, setIsSubmitting] = useState(false)

  const handleFeedback = useCallback(
    async (isHelpful: boolean) => {
      if (isSubmitting || feedbackState === 'submitted') return

      setIsSubmitting(true)
      setFeedbackState(isHelpful ? 'positive' : 'negative')

      try {
        const rating = isHelpful ? 5 : 1
        const feedbackType: ZoneQAFeedbackType = isHelpful
          ? 'helpful'
          : 'not_helpful'
        await onSubmit(messageId, rating, feedbackType)
        setFeedbackState('submitted')
      } catch (err) {
        console.error('[ZoneQAFeedback] Error submitting feedback:', err)
        setFeedbackState('error')
      } finally {
        setIsSubmitting(false)
      }
    },
    [messageId, onSubmit, isSubmitting, feedbackState]
  )

  if (feedbackState === 'submitted') {
    return (
      <FeedbackContainer>
        <ThankYouMessage>
          <Icon name="check-circle" size="small" />
          {t.thankYou}
        </ThankYouMessage>
      </FeedbackContainer>
    )
  }

  if (feedbackState === 'error') {
    return (
      <FeedbackContainer>
        <ErrorMessage>{t.feedbackError}</ErrorMessage>
      </FeedbackContainer>
    )
  }

  return (
    <FeedbackContainer>
      <FeedbackButton
        $variant="positive"
        $isSelected={feedbackState === 'positive'}
        onClick={() => handleFeedback(true)}
        disabled={disabled || isSubmitting}
        type="button"
        aria-label={t.helpful}
      >
        <Icon name="thumbs-up" size="small" />
        {t.helpful}
      </FeedbackButton>
      <FeedbackButton
        $variant="negative"
        $isSelected={feedbackState === 'negative'}
        onClick={() => handleFeedback(false)}
        disabled={disabled || isSubmitting}
        type="button"
        aria-label={t.notHelpful}
      >
        <Icon name="thumbs-down" size="small" />
        {t.notHelpful}
      </FeedbackButton>
    </FeedbackContainer>
  )
}
